import type { SaveOfferBuilderRoomParams } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import type { OpportunityRoom } from '~/hooks/apis/offers/useGetOffer';

import type { FlashOfferRoomDraft } from './utils';

type RoomDraftPayload = SaveOfferBuilderRoomParams['room'];
type RoomBedGroup = NonNullable<RoomDraftPayload['bed_groups']>[number];
type RoomBedConfiguration = NonNullable<RoomBedGroup['configuration']>[number];

export type SalesforceRoomSource = Pick<
  OpportunityRoom,
  'bed_groups' | 'id' | 'name' | 'room_asset' | 'room_size__c' | 'sfid'
>;

type GeneratedBeddingConfigurationResponse = {
  result?: {
    bed_grouping?: Array<Array<{ bed_type?: string; quantity?: number }>>;
  };
};

export function getStringValue(value: string | number | null | undefined) {
  const trimmedValue = value === undefined || value === null ? undefined : String(value).trim();
  return trimmedValue || undefined;
}

function getRoomSizeSqm(value: string | number | null | undefined) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.round(value) : null;
  }

  const roomSizeValue = getStringValue(value);
  const matchedValue = roomSizeValue?.match(/\d+(?:\.\d+)?/)?.[0];
  const roomSize = matchedValue ? Number(matchedValue) : null;

  if (!roomSize || !Number.isFinite(roomSize)) {
    return null;
  }

  return Math.round(roomSize);
}

export function getRoomBedGroups(sourceRooms: Array<SalesforceRoomSource>) {
  return sourceRooms.find((room) => room.bed_groups?.length)?.bed_groups ?? [];
}

export function getSalesforceRoomSizeSqm(room: SalesforceRoomSource) {
  return getRoomSizeSqm(room.room_size__c) ?? getRoomSizeSqm(room.room_asset?.room_size__c);
}

function buildBedDescription(configuration: RoomBedConfiguration) {
  if (!configuration.quantity || !configuration.type) {
    return null;
  }

  const typeWithoutBed = String(configuration.type).replace('Bed', '');
  const formattedType = typeWithoutBed
    .replace('DoubleSofa', 'Double Sofa')
    .replace('QueenSofa', 'Queen Sofa')
    .replace('RollAway', 'Roll Away');

  return `${configuration.quantity} ${formattedType} Bed${configuration.quantity > 1 ? 's' : ''}`;
}

export function getBedGroupDescription(bedGroup: RoomBedGroup) {
  const descriptionParts = (bedGroup.configuration ?? []).map((configuration) => buildBedDescription(configuration));
  const descriptionValues = descriptionParts.filter((description): description is string => !!description);
  const lastDescription = descriptionValues.length > 1 ? descriptionValues.pop() : null;

  return `${descriptionValues.join(', ')}${lastDescription ? ` and ${lastDescription}` : ''}`;
}

export function getNumberOfBeds(bedGroups: Array<RoomBedGroup>) {
  return bedGroups.map((bedGroup) =>
    (bedGroup.configuration ?? []).reduce((total, configuration) => total + (configuration.quantity || 0), 0),
  );
}

export function normaliseRoomDraftFormData(formData: Partial<FlashOfferRoomDraft>): Partial<FlashOfferRoomDraft> {
  const bedGroups = (formData.bed_groups ?? []).map((bedGroup) => ({
    ...bedGroup,
    description: getBedGroupDescription(bedGroup),
  }));

  return {
    ...formData,
    bed_groups: bedGroups,
    ...(bedGroups.length > 0 ? { number_of_beds: getNumberOfBeds(bedGroups) } : {}),
  };
}

export function mapGeneratedBeddingConfigurationToBedGroups(
  response: GeneratedBeddingConfigurationResponse | undefined,
): NonNullable<RoomDraftPayload['bed_groups']> {
  return (
    response?.result?.bed_grouping?.map((group) => ({
      configuration: group.map((bed) => ({
        quantity: bed.quantity,
        type: bed.bed_type,
      })),
    })) ?? []
  );
}
